import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Collapse,
    FormControl,
    FormControlLabel,
    Grid,
    MenuItem,
    OutlinedInput,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    TextareaAutosize,
} from "@mui/material";
import { toast } from "react-toastify";
import ChoiceInput from "./ChoiceInput";
import { MULTI_SELECT_INPUT_TYPES } from "../../constants";
import { QuestionType, MIN_RATING_CHOICES, MAX_RATING_CHOICES } from "@fhc/shared/constants";
import appApi from "api";
import DropDownIcon from "components/Icons/DropDownIcon";
import Button from "components/UserInput/Buttons/Button";
import List from "components/List";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const questionTypes = [
    {
        name: "Single Response",
        value: QuestionType.SINGLE_RESPONSE,
    },
    {
        name: "Multiple Response",
        value: QuestionType.MULTIPLE_RESPONSE,
    },
    {
        name: "Rating Question",
        value: QuestionType.RATING_SCALE,
    },
    {
        name: "Textbox Question",
        value: QuestionType.TEXTBOX,
    },
];

const DEFAULT_CHOICE = Object.freeze({
    label: "",
    value: 1,
    inputType: null,
});

const DEFAULT_FORM_VALUES = Object.freeze({
    questionType: QuestionType.SINGLE_RESPONSE,
    question: "",
    secondary_question: "",
    choices: [{ ...DEFAULT_CHOICE }],
    radioChoices: [],
});

const DEFAULT_NUM_RATING_SCALE_CHOICES = 5;

/**
 * @param {object} props
 * @param {(question: object) => void} [props.onQuestionAdded] Called when a question has been added. The function won't be called if adding a question fails.
 * @param {(question: object) => void} [props.onQuestionUpdated] Called when a question has been updated. The function won't be called if updating a question fails.
 * @param {() => void} [props.onCancel] Called when the user clicks the cancel button.
 * @param {boolean} props.disableClearFormOnCancel If `true` the form will not be cleared when the cancel but is clicked. Useful for displaying the form in a modal the cleared form will be visible for a split second before the modal is closed.
 * @param {{ id: number; question: string; secondary_question: string; choices: { label: string, value: string, inputType: string }[]; radioChoices: object, questionType: string }} props.questionToEdit If provided, the form will be pre-populated with this question's data and the form will be in update/edit mode.
 */
function QuestionForm({ onQuestionAdded, onQuestionUpdated, onCancel, disableClearFormOnCancel, questionToEdit }) {
    const [questionForm, setQuestionForm] = useState(
        questionToEdit
            ? {
                  ...questionToEdit,
                  choices: questionToEdit.choices.filter(
                      (choice) => !choice.inputType || choice.inputType === MULTI_SELECT_INPUT_TYPES.checkbox,
                  ),
                  radioChoices: questionToEdit.choices.filter(
                      (choice) => choice.inputType === MULTI_SELECT_INPUT_TYPES.radio,
                  ),
              }
            : { ...DEFAULT_FORM_VALUES },
    );

    useEffect(() => {
        console.debug(questionForm);
    }, [questionForm]);

    const [loading, setLoading] = useState(false);

    // Separate state variables for input and range so we can wait to update the UI until the textbox becomes unfocused
    const numRatingScaleChoices =
        questionForm.questionType === QuestionType.RATING_SCALE
            ? questionForm.choices.length
            : DEFAULT_NUM_RATING_SCALE_CHOICES;
    const [ratingScaleRangeChoices, setRatingScaleRangeChoices] = useState(
        getRatingScaleChoices(numRatingScaleChoices),
    );
    const [ratingScaleRangeInput, setRatingScaleRangeInput] = useState(numRatingScaleChoices);

    const [showRadioOptions, setShowRadioOptions] = useState(questionForm.radioChoices?.length > 0);

    const [alertMessage, setAlertMessage] = useState({ success: false, message: "" });

    const onChangeQuestionType = (questionType) => {
        setQuestionForm((prevForm) => ({
            ...prevForm,
            questionType,
            choices: prevForm.choices.map((choice) => ({
                ...choice,
                inputType: questionType === QuestionType.MULTIPLE_RESPONSE ? MULTI_SELECT_INPUT_TYPES.checkbox : null,
            })),
            radioChoices: [],
        }));
    };

    const clearForms = () => {
        setQuestionForm({
            questionType: QuestionType.SINGLE_RESPONSE,
            question: "",
            secondary_question: "",
            choices: [{ ...DEFAULT_CHOICE, inputType: MULTI_SELECT_INPUT_TYPES.checkbox }],
            radioChoices: [],
        });
    };

    const saveQuestion = async () => {
        setLoading(true);

        const payload = {
            ...questionForm,
            choices:
                questionForm.questionType === QuestionType.RATING_SCALE
                    ? ratingScaleRangeChoices
                    : questionForm.questionType === QuestionType.SINGLE_RESPONSE
                      ? questionForm.choices.map((choice) => ({ label: choice.label, value: choice.value })) // Don't need input type for single select questions because they're always dropdowns
                      : questionForm.questionType === QuestionType.MULTIPLE_RESPONSE && [
                            ...questionForm.choices,
                            ...questionForm.radioChoices,
                        ],
        };

        if (questionToEdit) {
            // Backend expects choice values to be integers
            payload.choices = payload.choices.map((choice) => ({ ...choice, value: Number(choice.value) }));

            const response = await appApi.put(`/question/${questionToEdit.id}`, payload);
            setLoading(false);
            if (response.data.success) {
                toast.success("Question updated");

                return onQuestionUpdated(response.data.data);
            } else {
                return setAlertMessage({
                    success: false,
                    message: <List items={response.data.errors} />,
                });
            }
        } else {
            const response = await appApi.post("/question", payload);
            setLoading(false);
            if (response.data.success) {
                toast.success("Question added");
                onQuestionAdded(response.data.data);
                clearForms();
                return;
            } else {
                return setAlertMessage({
                    success: false,
                    message: <List items={response.data.errors} />,
                });
            }
        }
    };

    /**
     * Returns max value + 1 if in edit mode, otherwise returns null. Justification for returning null: the
     * backend will handle generating values when creating/adding a question.
     */
    function getValueForAnswer() {
        if (!questionToEdit) {
            return null;
        }

        const values = [
            ...questionForm.choices.map((choice) => Number(choice.value)),
            ...questionForm.radioChoices.map((choice) => Number(choice.value)),
        ].filter((value) => !isNaN(value));

        return (Math.max(...values) + 1).toString();
    }

    const addChoice = (inputType) => {
        const value = getValueForAnswer();

        if (!inputType || inputType === MULTI_SELECT_INPUT_TYPES.checkbox) {
            setQuestionForm((prevForm) => ({
                ...prevForm,
                choices: [...prevForm.choices, { ...DEFAULT_CHOICE, inputType, value }],
            }));
        } else if (inputType === MULTI_SELECT_INPUT_TYPES.radio) {
            const defaultRadioChoice = {
                ...DEFAULT_CHOICE,
                inputType: MULTI_SELECT_INPUT_TYPES.radio,
            };

            setQuestionForm((prevForm) => ({
                ...prevForm,
                radioChoices: [...prevForm.radioChoices, { ...defaultRadioChoice, inputType, value }],
            }));
        } else {
            throw new Error("Invalid input type");
        }
    };

    const changeChoiceLabel = (label, index, inputType) => {
        if (!inputType || inputType === MULTI_SELECT_INPUT_TYPES.checkbox) {
            setQuestionForm((prevForm) => {
                const newChoices = [...prevForm.choices];
                newChoices[index].label = label;
                return {
                    ...prevForm,
                    choices: newChoices,
                };
            });
        } else if (inputType === MULTI_SELECT_INPUT_TYPES.radio) {
            setQuestionForm((prevForm) => {
                const newRadioChoices = [...prevForm.radioChoices];
                newRadioChoices[index].label = label;
                return {
                    ...prevForm,
                    radioChoices: newRadioChoices,
                };
            });
        } else {
            throw new Error("Invalid input type");
        }
    };

    function changeChoiceValue(value, index, inputType) {
        if (!inputType || inputType === MULTI_SELECT_INPUT_TYPES.checkbox) {
            setQuestionForm((prevForm) => {
                const newChoices = [...prevForm.choices];
                newChoices[index].value = value;
                return {
                    ...prevForm,
                    choices: newChoices,
                };
            });
        } else if (inputType === MULTI_SELECT_INPUT_TYPES.radio) {
            setQuestionForm((prevForm) => {
                const newRadioChoices = [...prevForm.radioChoices];
                newRadioChoices[index].value = value;
                return {
                    ...prevForm,
                    radioChoices: newRadioChoices,
                };
            });
        } else {
            throw new Error("Invalid input type");
        }
    }

    /**
     * @param {number} newRangeInput
     */
    function onRatingScaleRangeInputUnfocus(newRangeInput) {
        // Limit the range input within the min and max
        let _newRangeInput = newRangeInput;
        if (_newRangeInput < MIN_RATING_CHOICES) {
            _newRangeInput = MIN_RATING_CHOICES;
        } else if (_newRangeInput > MAX_RATING_CHOICES) {
            _newRangeInput = MAX_RATING_CHOICES;
        }

        setRatingScaleRangeInput(_newRangeInput);

        const choices = getRatingScaleChoices(_newRangeInput);
        setRatingScaleRangeChoices(choices);
    }

    /**
     * @param {number} newRangeInput
     */
    function onRatingScaleRangeInputChange(newRangeInput) {
        setRatingScaleRangeInput(newRangeInput);

        // Only update the rating scale when user input changes if it's within the min and max range. If the user
        // input exceeds the min or max range, the UI will be updated to the min or max when the textbox is unfocused
        if (newRangeInput >= MIN_RATING_CHOICES && newRangeInput <= MAX_RATING_CHOICES) {
            const choices = getRatingScaleChoices(newRangeInput);
            setRatingScaleRangeChoices(choices);
        }
    }

    /**
     * @param {number} range
     */
    function getRatingScaleChoices(range) {
        let choices = [];

        for (let i = 1; i < range + 1; i++) {
            if (i === 1) {
                choices.push({ value: i, label: "Not good at all 1" });
            } else if (range === i) {
                choices.push({ value: i, label: `Very good ${i}` });
            } else {
                choices.push({ value: i, label: i });
            }
        }

        return choices;
    }

    async function onCheckAddRadioChoices() {
        if (showRadioOptions) {
            // Remove all radio choices if the checkbox is being unchecked
            setQuestionForm((prevForm) => ({
                ...prevForm,
                radioChoices: [],
            }));
        } else {
            setQuestionForm((prevForm) => ({
                ...prevForm,
                radioChoices: [
                    ...prevForm.radioChoices,
                    { ...DEFAULT_CHOICE, inputType: MULTI_SELECT_INPUT_TYPES.radio, value: getValueForAnswer() },
                ],
            }));
        }

        setShowRadioOptions(!showRadioOptions);
    }

    const inputType = useMemo(
        () => (questionForm.questionType === QuestionType.MULTIPLE_RESPONSE ? MULTI_SELECT_INPUT_TYPES.checkbox : null),
        [questionForm.questionType],
    );

    const choiceInputSharedProps = {
        onChangeLabel: changeChoiceLabel,
        onChangeValue: changeChoiceValue,
        onAdd: addChoice,
        isUpdateMode: Boolean(questionToEdit),
    };

    return (
        <Grid container spacing={2} className="overflow-visible">
            <Grid item xs={12}>
                <FormControl sx={{ width: "85%" }} size="medium">
                    <Select
                        IconComponent={() => (
                            <div className="mx-[9px]">
                                <DropDownIcon />
                            </div>
                        )}
                        className="w-full aq-question-settings"
                        value={questionForm.questionType}
                        onChange={(event) => onChangeQuestionType(event.target.value)}
                        input={<OutlinedInput label="" />}
                        MenuProps={MenuProps}
                        sx={{
                            color: "#3D4A5C",
                            ".MuiOutlinedInput-notchedOutline": {
                                borderColor: "transparent",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "transparent",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "transparent",
                            },
                            ".MuiSvgIcon-root ": {
                                fill: "#D7DFE9 !important",
                            },
                        }}
                    >
                        {questionTypes.map((type, i) => (
                            <MenuItem key={type.value} value={type.value}>
                                {type.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12}>
                <FormControl sx={{ width: "85%" }}>
                    <input
                        className="aq-question-settings px-[14px] py-[30px]"
                        value={questionForm.question}
                        placeholder="*Heading"
                        onChange={(e) => setQuestionForm((form) => ({ ...form, question: e.target.value }))}
                    />
                </FormControl>
            </Grid>

            <Grid item xs={12} className="mb-[27px]">
                <FormControl sx={{ width: "85%" }}>
                    <TextareaAutosize
                        className="aq-question-settings"
                        value={questionForm.secondary_question}
                        onChange={(e) => setQuestionForm((form) => ({ ...form, secondary_question: e.target.value }))}
                        minRows={3}
                        placeholder="Question"
                        style={{ padding: "10px", height: 85 }}
                    />
                </FormControl>
            </Grid>

            {questionForm.questionType === QuestionType.MULTIPLE_RESPONSE ||
            questionForm.questionType === QuestionType.SINGLE_RESPONSE ? (
                <Grid item xs={12} className="pt-0">
                    {questionToEdit && (
                        <div
                            className="paragraph-5-regular"
                            style={{ display: "flex", color: "var(--colors-primary-slate-600)" }}
                        >
                            <span style={{ width: "60px" }}>Value</span>
                            <span style={{ marginLeft: "16px" }}>Answer</span>
                        </div>
                    )}

                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        <>
                            {questionForm.choices.map((choice, index) => (
                                <ChoiceInput
                                    key={`checkbox-choice-input-${index}`}
                                    choice={choice}
                                    index={index}
                                    onRemove={() => {
                                        setQuestionForm((prevForm) => ({
                                            ...prevForm,
                                            choices: prevForm.choices.filter((_, i) => i !== index),
                                        }));
                                    }}
                                    showRemoveButton={questionForm.choices.length > 1}
                                    inputType={inputType}
                                    {...choiceInputSharedProps}
                                />
                            ))}

                            {questionForm.questionType === QuestionType.MULTIPLE_RESPONSE && (
                                <>
                                    <div className="flex gap-2 items-center">
                                        <input
                                            id="add-radio-options-checkbox"
                                            type="checkbox"
                                            checked={showRadioOptions}
                                            onChange={onCheckAddRadioChoices}
                                        />
                                        <label className="paragraph-5-regular" htmlFor="add-radio-options-checkbox">
                                            Add a radio option(s) to the question
                                        </label>
                                    </div>
                                </>
                            )}

                            {showRadioOptions && (
                                <>
                                    <hr className="mx-[-32px]" />
                                    {questionForm.radioChoices.map((radioChoice, index) => (
                                        <ChoiceInput
                                            key={`radio-choice-input-${index}`}
                                            choice={radioChoice}
                                            index={index}
                                            onRemove={() => {
                                                setQuestionForm((prevForm) => ({
                                                    ...prevForm,
                                                    radioChoices: prevForm.radioChoices.filter((_, i) => i !== index),
                                                }));
                                            }}
                                            showRemoveButton={questionForm.radioChoices.length > 1}
                                            inputType={MULTI_SELECT_INPUT_TYPES.radio}
                                            {...choiceInputSharedProps}
                                        />
                                    ))}
                                </>
                            )}
                        </>
                    </div>
                </Grid>
            ) : (
                questionForm.questionType === QuestionType.RATING_SCALE && (
                    <>
                        <Grid item xs={12}>
                            <FormControl sx={{ m: 1, width: 200 }} size="small">
                                <TextField
                                    variant="outlined"
                                    placeholder="Number of choices"
                                    type="number"
                                    value={ratingScaleRangeInput}
                                    onBlur={() => onRatingScaleRangeInputUnfocus(ratingScaleRangeInput)}
                                    onChange={(e) => onRatingScaleRangeInputChange(parseInt(e.target.value))}
                                ></TextField>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <RadioGroup sx={{ m: 1 }}>
                                <TableContainer component={Paper} sx={{ width: "100%" }}>
                                    <Table sx={{ width: "100%" }}>
                                        <TableHead>
                                            <TableRow>
                                                {ratingScaleRangeChoices.map((choice, index) => (
                                                    <TableCell key={index} align="center">
                                                        {choice.label}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                {ratingScaleRangeChoices.map((choice, index) => (
                                                    <TableCell key={index} align="center">
                                                        <FormControlLabel
                                                            className="margin-0"
                                                            value={choice.value}
                                                            control={<Radio />}
                                                        />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </RadioGroup>
                        </Grid>
                    </>
                )
            )}

            <Grid item xs={12}>
                <Collapse sx={{ m: 1 }} in={!!alertMessage.message}>
                    {alertMessage.success ? (
                        <Alert>{alertMessage.message}</Alert>
                    ) : (
                        <Alert severity="error">{alertMessage.message}</Alert>
                    )}
                </Collapse>
                <div className="flex items-center space-x-2">
                    <FormControl sx={{ m: 1 }} size="medium">
                        <Button size="xs" handleOnClick={saveQuestion} loading={loading}>
                            {questionToEdit ? "Update" : "Save Question"}
                        </Button>
                    </FormControl>
                    <FormControl sx={{ m: 1 }} size="medium">
                        <Button
                            size="xs"
                            colorTheme="secondary"
                            handleOnClick={() => {
                                if (!disableClearFormOnCancel) {
                                    clearForms();
                                }
                                onCancel?.();
                            }}
                        >
                            {questionToEdit ? "Cancel" : "Delete"}
                        </Button>
                    </FormControl>
                </div>
            </Grid>
        </Grid>
    );
}

export default QuestionForm;
